import React, { useState } from 'react';
import { Button, Input, InputNumber, Table, Space, message, Dropdown, Modal, Popover } from 'antd';
import type { MenuProps } from 'antd';
import { SearchOutlined, DownOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import s from './s.module.less';
import AddAndEditStaff from 'components/AddAndEditStaff';
import { updateTeamMemberStatus, pauseListing, resumeListing, remindTeamMember } from 'api/group';
import { EPortalStatus, TTeamMemberDto } from 'types/group';
import { ERoleDisplayName } from 'types/enumItems';
import useAllTeamMember from 'hooks/group/useAllTeamMember';
import useCurrentUser from 'hooks/useCurrentUser';
import AddProviderModal from 'components/AddProviderModal';
import EditProviderModal from 'components/EditProviderModal';
import { PATH } from 'constants/path';
import { closeManageSubscriptionModal } from 'utils/globalLayerControl';
import CommonTable from 'components/CommonTable';
import { canShowPracticeGroupFuns, canShowTeamManagementAction } from 'utils/common';
import HomeStore from 'store/Home';
import { canShowEditAction } from './memberActions';
import commonS from 'styles/common.module.less';

type Props = {};

const ADMIN_ROLE_CODES = ['GROUP_PRACTICE_MANAGER', 'GROUP_OWNER'];

const STATUS_CONFIG: Record<string, { color: string; bg: string }> = {
    ACTIVE: { color: '#15803d', bg: '#dcfce7' },
    PENDING: { color: '#c2410c', bg: '#fff7ed' },
    DISABLED: { color: '#6b7280', bg: '#f3f4f6' },
    PAUSED: { color: '#92400e', bg: '#fef3c7' },
};

const STATUS_DISPLAY: Record<string, string> = {
    ACTIVE: 'Active',
    PENDING: 'Pending',
    DISABLED: 'Disabled',
    PAUSED: 'Paused',
};

const ROLE_CODE_DISPLAY: Record<string, string> = {
    GROUP_OWNER: 'Group owner',
    GROUP_ADMINISTRATIVE_ASSISTANT: ERoleDisplayName.GROUP_ADMINISTRATIVE_ASSISTANT,
    GROUP_CLINICAL_ASSISTANT: ERoleDisplayName.GROUP_CLINICAL_ASSISTANT,
    GROUP_PRACTICE_MANAGER: ERoleDisplayName.GROUP_PRACTICE_MANAGER,
    GROUP_PROVIDER: ERoleDisplayName.GROUP_PROVIDER,
};

type PauseOption = '1day' | 'custom' | 'indefinitely';

const PauseListingPopover: React.FC<{
    record: TTeamMemberDto;
    onSave: (record: TTeamMemberDto, option: PauseOption, days?: number) => void;
    children: React.ReactNode;
}> = ({ record, onSave, children }) => {
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState<PauseOption>('1day');
    const [customDays, setCustomDays] = useState<number | null>(null);

    const handleSave = () => {
        onSave(record, selected, selected === 'custom' ? customDays || 1 : undefined);
        setOpen(false);
        setSelected('1day');
        setCustomDays(null);
    };

    const content = (
        <div className={s.pausePopover}>
            <div
                className={`${s.pauseOption} ${selected === '1day' ? s.pauseOptionSelected : ''}`}
                onClick={() => setSelected('1day')}
            >
                <span>Pause for 1 day</span>
                {selected === '1day' && <span className={s.pauseCheck}>&#10003;</span>}
            </div>
            <div
                className={`${s.pauseOption} ${selected === 'custom' ? s.pauseOptionSelected : ''}`}
                onClick={() => setSelected('custom')}
            >
                <span>Pause for</span>
                <InputNumber
                    min={1}
                    max={365}
                    value={customDays}
                    onChange={(v) => {
                        setCustomDays(v);
                        setSelected('custom');
                    }}
                    className={s.pauseDaysInput}
                    size="small"
                />
                <span>days</span>
                {selected === 'custom' && <span className={s.pauseCheck}>&#10003;</span>}
            </div>
            <div
                className={`${s.pauseOption} ${selected === 'indefinitely' ? s.pauseOptionSelected : ''}`}
                onClick={() => setSelected('indefinitely')}
            >
                <span>Pause indefinitely</span>
                {selected === 'indefinitely' && <span className={s.pauseCheck}>&#10003;</span>}
            </div>
            <div className={s.pauseActions}>
                <Button onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="primary" onClick={handleSave}>
                    Save
                </Button>
            </div>
        </div>
    );

    return (
        <Popover
            content={content}
            trigger="click"
            open={open}
            onOpenChange={setOpen}
            placement="bottomRight"
            overlayInnerStyle={{ padding: 0 }}
        >
            {children}
        </Popover>
    );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TeamManagement = (props: Props) => {
    const navigate = useNavigate();
    const [currentUser] = useCurrentUser();
    const [getHomeStore] = HomeStore.useStore();
    const isPPAProvider = !!getHomeStore('data')?.ppaProvider;
    const [members, , refreshMembers] = useAllTeamMember();
    const [showAddStaffModal, setShowAddStaffModal] = useState(false);
    const [editingMember, setEditingMember] = useState<TTeamMemberDto | undefined>(undefined);
    const [showAddProviderModal, setShowAddProviderModal] = useState(false);
    const [showEditProviderModal, setShowEditProviderModal] = useState(false);
    const [remindingId, setRemindingId] = useState<number | null>(null);

    const [pendingNameFilter, setPendingNameFilter] = useState('');
    const [nameFilter, setNameFilter] = useState('');
    const [roleFilter, setRoleFilter] = useState<string | undefined>(undefined);
    const [providerFilter, setProviderFilter] = useState<string | undefined>(undefined);
    const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
    const [showHelpModal, setShowHelpModal] = useState(false);
    //所有的人都可以添加，编辑只有 owner/practice admin/admin
    const showTeamManagementAction = canShowTeamManagementAction(currentUser) || true;
    const canEdit =
        currentUser?.roleCode === 'GROUP_OWNER' ||
        currentUser?.roleCode === 'GROUP_PRACTICE_MANAGER' ||
        currentUser?.isAdmin;
    const isAdmin = currentUser?.isAdmin || ADMIN_ROLE_CODES.includes(currentUser?.roleCode as string);

    React.useEffect(() => {
        const cls = 'hide-manage-subscription-modal';
        if (showAddStaffModal || showEditProviderModal || showAddProviderModal || showHelpModal) {
            document.body.classList.add(cls);
        } else {
            document.body.classList.remove(cls);
        }
        return () => document.body.classList.remove(cls);
    }, [showAddStaffModal, showEditProviderModal, showAddProviderModal, showHelpModal]);

    const handleAddStaff = () => {
        setEditingMember(undefined);
        setShowAddStaffModal(true);
    };

    const handleAddProviders = () => {
        setShowAddProviderModal(true);
    };

    const handleCopyStaffInviteLink = () => {
        // TODO: replace with actual invite link when API ready
        navigator.clipboard.writeText('https://example.com/invite/staff');
        message.success('Staff invitation link copied to clipboard');
    };

    const handleCopyProviderInviteLink = () => {
        // TODO: replace with actual invite link when API ready
        navigator.clipboard.writeText('https://example.com/invite/provider');
        message.success('Provider invitation link copied to clipboard');
    };

    const addMemberMenuItems: MenuProps['items'] = [
        {
            key: 'add-staff',
            label: 'Add a staff',
            onClick: handleAddStaff,
        },
        ...(canShowPracticeGroupFuns(currentUser) && isPPAProvider
            ? [
                  {
                      key: 'add-providers',
                      label: 'Add a provider',
                      onClick: handleAddProviders,
                  },
              ]
            : []),
    ];

    const copyLinkMenuItems: MenuProps['items'] = [
        {
            key: 'invite-staff',
            label: 'Invite staff',
            onClick: handleCopyStaffInviteLink,
        },
        {
            key: 'invite-provider',
            label: (
                <span>
                    Invite provider <span className={s.newBadge}>New</span>
                </span>
            ),
            onClick: handleCopyProviderInviteLink,
        },
    ];

    const handleResendInvite = async (record: TTeamMemberDto) => {
        if (!record.id) return;
        setRemindingId(record.id);
        try {
            const res = await remindTeamMember(record.id);
            if (res?.error) {
                message.error(res.error);
            } else {
                message.success('Invite resent');
            }
        } catch (e) {
            message.error('Failed to resend invite');
        } finally {
            setRemindingId(null);
        }
    };

    const handlePauseListing = async (record: TTeamMemberDto, option: PauseOption, days?: number) => {
        if (!record.providerId) return;
        let pauseDays: number | undefined;
        if (option === '1day') pauseDays = 1;
        else if (option === 'custom') pauseDays = days || 1;

        const res = await pauseListing({ providerId: record.providerId, pauseDays });
        if (res.error) return;

        const reactivateAt = res?.data?.data?.reactivateAt;
        if (reactivateAt) {
            const dateStr = new Date(reactivateAt).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            });
            message.success(`Provider paused successfully. Their listing will be reactivated on ${dateStr}.`);
        } else {
            message.success('Provider paused successfully.');
        }
        refreshMembers();
    };

    const handleResumeListing = async (record: TTeamMemberDto) => {
        if (!record.providerId) return;
        const res = await resumeListing({ providerId: record.providerId });
        if (res.error) return;
        message.success('Provider resumed successfully. Their listing is now visible to patients.');
        refreshMembers();
    };

    const handleEditTeamMember = (record: TTeamMemberDto) => {
        setEditingMember(record);
        if (record.roleCode === 'GROUP_PROVIDER') {
            setShowEditProviderModal(true);
        } else {
            setShowAddStaffModal(true);
        }
    };

    const handleDeactivate = (record: TTeamMemberDto) => {
        Modal.confirm({
            title: 'Deactivate team member',
            content: `Are you sure you want to deactivate ${record.firstName} ${record.lastName}? They will lose access to the practice portal.`,
            okText: 'Deactivate',
            okButtonProps: { danger: true },
            onOk: async () => {
                const res = await updateTeamMemberStatus(record.id, 'DISABLED');
                if (res.error) return;
                message.success('Member deactivated');
                refreshMembers();
            },
        });
    };

    const handleReactivate = (record: TTeamMemberDto) => {
        Modal.confirm({
            title: 'Reactivate team member',
            content: `Are you sure you want to reactivate ${record.firstName} ${record.lastName}?`,
            okText: 'Reactivate',
            onOk: async () => {
                const res = await updateTeamMemberStatus(record.id, 'ACTIVE');
                if (res.error) return;
                message.success('Member reactivated');
                refreshMembers();
            },
        });
    };

    const filteredMembers = members.filter((m) => {
        if (nameFilter) {
            const name = `${m.firstName} ${m.lastName}`.toLowerCase();
            const email = (m.email || '').toLowerCase();
            if (!name.includes(nameFilter.toLowerCase()) && !email.includes(nameFilter.toLowerCase())) {
                return false;
            }
        }
        if (roleFilter && m.roleCode !== roleFilter) return false;
        if (providerFilter === 'all') {
            if (!m.isAllProviders) return false;
        } else if (providerFilter) {
            const pid = Number(providerFilter);
            if (m.isAllProviders) return true;
            if (!m.assignedProviders?.some((p) => p.providerId === pid)) return false;
        }
        if (statusFilter === 'PAUSED') {
            if (m.listingStatus === 'PL') return true;
        } else if (statusFilter && m.status !== statusFilter) return false;
        return true;
    });

    const providerMembers = members.filter((m) => m.roleCode === 'GROUP_PROVIDER');

    const roleCodeOptions = Object.keys(ROLE_CODE_DISPLAY);
    const statusOptions = Object.keys(STATUS_DISPLAY);

    const tableColumns: ColumnsType<TTeamMemberDto> = [
        {
            title: 'NAME',
            dataIndex: 'name',
            key: 'name',
            width: 200,
            filterSearch: true,
            fixed: 'left',
            filterIcon: <SearchOutlined />,
            filterDropdown: () => (
                <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Input
                        value={pendingNameFilter}
                        onChange={(e) => setPendingNameFilter(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                setNameFilter(pendingNameFilter);
                            }
                        }}
                        placeholder="Search name"
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => setNameFilter(pendingNameFilter)}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Search
                        </Button>
                        <Button
                            onClick={() => {
                                setPendingNameFilter('');
                                setNameFilter('');
                            }}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Reset
                        </Button>
                    </Space>
                </div>
            ),
            render: (_, record) => (
                <div className={s.nameCell}>
                    <div className={s.memberAvatarInitials}>
                        {record.firstName?.charAt(0)}
                        {record.lastName?.charAt(0)}
                    </div>
                    <div className={s.nameInfo}>
                        <span className={s.memberName}>{`${record.firstName} ${record.lastName}`}</span>
                        {record.email && <span className={s.memberEmail}>{record.email}</span>}
                    </div>
                </div>
            ),
        },
        {
            title: 'ROLE',
            dataIndex: 'roleCode',
            key: 'roleCode',
            width: 200,
            filterDropdown: ({ close }) => (
                <div className={s.dropWrap}>
                    <div
                        className={s.dropItem}
                        onClick={() => {
                            setRoleFilter(undefined);
                            close();
                        }}
                    >
                        All roles
                    </div>
                    {roleCodeOptions.map((code) => (
                        <div
                            key={code}
                            className={s.dropItem}
                            onClick={() => {
                                setRoleFilter(code);
                                close();
                            }}
                        >
                            {ROLE_CODE_DISPLAY[code] || code}
                        </div>
                    ))}
                </div>
            ),
            render: (roleCode: string, record) => (
                <span className={s.roleCell}>
                    {ROLE_CODE_DISPLAY[roleCode] || roleCode}
                    {(ADMIN_ROLE_CODES.includes(roleCode) || record.isAdmin) && (
                        <span className={s.adminBadge}>Admin</span>
                    )}
                </span>
            ),
        },
        {
            title: 'PROVIDER ASSIGNED',
            key: 'assignedProviders',
            width: 220,
            filterDropdown: ({ close }) => (
                <div className={s.dropWrap}>
                    <div
                        className={s.dropItem}
                        onClick={() => {
                            setProviderFilter(undefined);
                            close();
                        }}
                    >
                        All
                    </div>
                    <div
                        className={s.dropItem}
                        onClick={() => {
                            setProviderFilter('all');
                            close();
                        }}
                    >
                        All providers
                    </div>
                    {providerMembers.map((p) => (
                        <div
                            key={p.id}
                            className={s.dropItem}
                            onClick={() => {
                                setProviderFilter(String(p.providerId));
                                close();
                            }}
                        >
                            {`${p.firstName} ${p.lastName}`}
                        </div>
                    ))}
                </div>
            ),
            render: (_, record) => {
                if (record.isAllProviders) return <span>All providers</span>;
                if (record.assignedProviders?.length) {
                    return (
                        <span>{record.assignedProviders.map((p) => `${p.firstName} ${p.lastName}`).join(', ')}</span>
                    );
                }
                return <span>-</span>;
            },
        },
        {
            title: 'STATUS',
            dataIndex: 'status',
            key: 'status',
            width: 120,
            filterDropdown: ({ close }) => (
                <div className={s.dropWrap}>
                    <div
                        className={s.dropItem}
                        onClick={() => {
                            setStatusFilter(undefined);
                            close();
                        }}
                    >
                        All statuses
                    </div>
                    {statusOptions.map((st) => (
                        <div
                            key={st}
                            className={s.dropItem}
                            onClick={() => {
                                setStatusFilter(st);
                                close();
                            }}
                        >
                            {STATUS_DISPLAY[st] || st}
                        </div>
                    ))}
                </div>
            ),
            render: (status: string, record) => {
                if (record.listingStatus === 'PL') {
                    const pausedConfig = STATUS_CONFIG.PAUSED || { color: '#92400e', bg: '#fef3c7' };
                    if (record.listingReactivateAt) {
                        const dateStr = new Date(record.listingReactivateAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                        });
                        return (
                            <span
                                className={s.statusTag}
                                style={{ color: pausedConfig.color, backgroundColor: pausedConfig.bg }}
                            >
                                Paused - Reactivated on {dateStr}
                            </span>
                        );
                    }
                    return (
                        <span
                            className={s.statusTag}
                            style={{ color: pausedConfig.color, backgroundColor: pausedConfig.bg }}
                        >
                            Paused - Indefinitely
                        </span>
                    );
                }
                const config = STATUS_CONFIG[status] || { color: '#6b7280', bg: '#f3f4f6' };
                return (
                    <span className={s.statusTag} style={{ color: config.color, backgroundColor: config.bg }}>
                        {STATUS_DISPLAY[status] || status}
                    </span>
                );
            },
        },
        {
            title: null,
            key: 'action',
            render: (_, record) => {
                const isPending = record.status === 'PENDING';
                const isInactive = record.status === 'DISABLED';
                const isActive = record.status === 'ACTIVE';
                const isOwner = record.roleCode === 'GROUP_OWNER';
                const isSelf = `${currentUser?.user?.id}` === `${record.userId}`;
                const isNotInvited = record.portalStatus === EPortalStatus.NOT_INVITED;
                if (isOwner || isSelf) {
                    return <span>-</span>;
                }
                if (isNotInvited && record.providerId) {
                    return (
                        <div className={s.actionCell}>
                            <Button
                                type="link"
                                onClick={() => {
                                    closeManageSubscriptionModal();
                                    navigate(`${PATH.REVIEW_PROVIDER_PROFILE}?providerIds=${record.providerId}`);
                                }}
                            >
                                Resume invite
                            </Button>
                        </div>
                    );
                }
                if (!isAdmin) {
                    if (!isPending) {
                        return <span>-</span>;
                    }
                    return (
                        <Button
                            type="link"
                            loading={remindingId === record.id}
                            onClick={() => handleResendInvite(record)}
                        >
                            Resend invite
                        </Button>
                    );
                }
                return (
                    <div className={s.actionCell}>
                        <span className={s.actionCol1}>
                            {isPending ? (
                                <Button
                                    type="link"
                                    loading={remindingId === record.id}
                                    onClick={() => handleResendInvite(record)}
                                >
                                    Resend invite
                                </Button>
                            ) : (
                                <span className={s.actionCol3}>
                                    {(() => {
                                        if (isActive) {
                                            return (
                                                <Button type="link" onClick={() => handleDeactivate(record)}>
                                                    Deactivate
                                                </Button>
                                            );
                                        }
                                        if (isInactive) {
                                            return (
                                                <Button type="link" onClick={() => handleReactivate(record)}>
                                                    Reactivate
                                                </Button>
                                            );
                                        }
                                        return '-';
                                    })()}
                                </span>
                            )}
                        </span>
                        {canShowEditAction(!!canEdit, record.status) && (
                            <span className={s.actionCol2}>
                                <Button type="link" onClick={() => handleEditTeamMember(record)}>
                                    Edit
                                </Button>
                            </span>
                        )}

                        {record.roleCode === 'GROUP_PROVIDER' && canShowPracticeGroupFuns(currentUser) && (
                            <span className={s.actionCol2}>
                                {record.listingStatus === 'PL' && (
                                    <Button type="link" onClick={() => handleResumeListing(record)}>
                                        Resume listing
                                    </Button>
                                )}
                                {record.listingStatus === 'L' && (
                                    <PauseListingPopover record={record} onSave={handlePauseListing}>
                                        <Button type="link">Pause listing</Button>
                                    </PauseListingPopover>
                                )}
                            </span>
                        )}
                    </div>
                );
            },
        },
    ];

    return (
        <>
            <div className={s.module}>
                <div className={s.tabContent}>
                    <div className={s.teamHeader}>
                        <div className={s.teamHeaderLeft}>
                            <h2 className={s.teamTitle}>Manage your team</h2>
                        </div>
                        <div className={s.teamHeaderRightOut}>
                            <Button onClick={() => setShowHelpModal(true)} className={s.help}>
                                How group practice works
                            </Button>
                            {showTeamManagementAction && (
                                <div className={s.teamHeaderRight}>
                                    <Dropdown menu={{ items: addMemberMenuItems }} trigger={['hover']}>
                                        <Button className={s.addMemberBtn}>
                                            Add a new team member <DownOutlined />
                                        </Button>
                                    </Dropdown>
                                    {/* <Dropdown menu={{ items: copyLinkMenuItems }} trigger={['hover']}>
                                <Button type="primary" className={s.copyLinkBtn}>
                                    Copy invitation link <DownOutlined />
                                </Button>
                            </Dropdown> */}
                                </div>
                            )}
                        </div>
                    </div>
                    <p className={s.teamDesc}>
                        Invite team members to help manage your practice. Control their access to schedules, messages,
                        and patient records.
                    </p>
                    <div className={s.tableWrap}>
                        <CommonTable<TTeamMemberDto>
                            columns={tableColumns}
                            rowKey="id"
                            pagination={false}
                            scroll={{ x: 'max-content' }}
                            locale={{
                                emptyText: <div className={s.emptyText}>No team members yet</div>,
                            }}
                            className={s.teamTable}
                            data={filteredMembers}
                        />
                    </div>
                </div>
            </div>
            {showAddStaffModal && (
                <AddAndEditStaff
                    show={showAddStaffModal}
                    item={editingMember}
                    onCannel={() => {
                        setShowAddStaffModal(false);
                        setEditingMember(undefined);
                    }}
                    onSuccess={() => {
                        setShowAddStaffModal(false);
                        setEditingMember(undefined);
                        refreshMembers();
                    }}
                />
            )}
            <AddProviderModal
                open={showAddProviderModal}
                onClose={() => {
                    setShowAddProviderModal(false);
                    refreshMembers();
                }}
            />
            {showEditProviderModal && editingMember?.providerId && (
                <EditProviderModal
                    open={showEditProviderModal}
                    providerId={editingMember.providerId}
                    teamMemberId={editingMember.id}
                    isAdmin={editingMember.isAdmin}
                    onClose={() => {
                        setShowEditProviderModal(false);
                        setEditingMember(undefined);
                    }}
                    onSuccess={() => {
                        setShowEditProviderModal(false);
                        setEditingMember(undefined);
                        refreshMembers();
                    }}
                />
            )}
            <Modal
                onCancel={() => {
                    setShowHelpModal(false);
                }}
                className={commonS.modalFixHeightWrap}
                footer={null}
                open={showHelpModal}
                width="800px"
            >
                <div className={s.mWrap}>
                    <iframe
                        src="https://klarityhealth.usttp.larksuite.com/wiki/CpXRwcBapi58LWkR8QYujLVytve?opendoc=1"
                        title="How group practice works"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    />
                </div>
            </Modal>
        </>
    );
};

export default TeamManagement;
